import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import FinancialService from "../../rest/financialService";
import { getCurrentTimestamp, getToday } from "../../utils/DateUtils";
import ClientService from "../../rest/clientService";
import CustomerService from "../../rest/customerService";
import PaymentService from "../../rest/paymentsService";
import { formatCurrency } from "../../utils/CurrencyUtils";
import PaymentSelect from "./paymentSelect";
import CustomButton from "../lib/customButton";
import AsaasSeal from "./asaasSeal";
import qs from 'qs';


const CreditRechargeModal = ({ isOpen, onClose, client, parents = [] }) => {
    const [editedCredit, setEditedCredit] = useState("");
    const [receivedValue, setReceivedValue] = useState("");
    const [selectedPayment, setSelectedPayment] = useState("");
    const [enabledPayments, setEnabledPayments] = useState([]);
    const [autoPrint, setAutoPrint] = useState(false);
    const [isOpened, setIsOpened] = useState(false);
    const [loading, setLoading] = useState(false);
    const [initLoading, setInitLoading] = useState(false);
    const [clientInfo, setClientInfo] = useState();
    const [asaasEnabled, setAsaasEnabled] = useState(false);
    const [selectedParentLogin, setSelectedParentLogin] = useState("");
    const [pixQr, setPixQr] = useState(null);
    const initialCredit = Number(client?.credit || 0);
    const operator = sessionStorage.getItem('name');

    useEffect(() => {
        if (!isOpen) return;
        const info = JSON.parse(sessionStorage.getItem('clientInfo'));
        setClientInfo(info);
        setPixQr(null);
        setSelectedPayment("");
        setEditedCredit("");
        setReceivedValue("");
        setInitLoading(true);

        const init = async () => {
            try {
                const clientCode = sessionStorage.getItem('clientCode');
                const [openedResult, prefsResult, integrationResult] = await Promise.allSettled([
                    FinancialService.checkIfOpen(getToday()),
                    ClientService.getClientAdminPreferences(),
                    PaymentService.getIntegrationStatus(clientCode),
                ]);

                if (openedResult.status === "fulfilled") setIsOpened(openedResult.value === true);

                if (prefsResult.status === "fulfilled") {
                    const preferences = prefsResult.value;
                    const enabled = preferences?.accepted_payments
                        ?.split(";")
                        .map(v => v.trim().toUpperCase())
                        .filter(v => v && v !== "CREDITO FOODZ") || [];
                    setAutoPrint(preferences?.enable_auto_print);
                    setEnabledPayments(enabled);
                }

                if (integrationResult.status === "fulfilled") {
                    setAsaasEnabled(integrationResult.value?.asaasEnabled === true);
                }
            } catch (err) {
                console.error("Erro ao iniciar modal de recarga:", err);
            } finally {
                setInitLoading(false);
            }
        };

        init();
    }, [isOpen]);

    useEffect(() => {
        if (parents.length === 1) setSelectedParentLogin(parents[0].login);
        else setSelectedParentLogin("");
    }, [parents]);

    if (!isOpen) return null;

    // Asaas PIX applies when integration is enabled and the client is a student.
    // No parent requirement — if no parent is linked, the student is used as payer.
    const isAsaasPix = asaasEnabled && selectedPayment === "PIX" && client?.role === "STUDENT";
    const payerLogin = selectedParentLogin || client?.login;

    function openReceipt(updatedCreditCharge) {
        const win = window.open('', '_blank', 'toolbar=no,location=no,width=340,height=600');

        const queryString = qs.stringify({
            customer: JSON.stringify(client.name),
            currentCredit: JSON.stringify(updatedCreditCharge.credit_afterwards),
            grade: JSON.stringify(client.grade),
            transactions: updatedCreditCharge,
            totalAmount: updatedCreditCharge.value,
            operator,
            clientInfo: JSON.stringify(clientInfo),
            auto_print: autoPrint
        });

        if (win) {
            win.location.href = `/comprovanteRecarga?${queryString}`;
        } else {
            alert("Não foi possível abrir a janela. Verifique se o popup está bloqueado.");
        }
    }

    const handleSaveClick = async () => {
        const sanitizedCredit = Math.abs(parseFloat(editedCredit)) || 0;
        const newValue = initialCredit + sanitizedCredit;

        if (sanitizedCredit <= 0) {
            alert("Informe um valor válido para recarga.");
            return;
        }

        if (!selectedPayment) {
            alert("Selecione uma forma de pagamento.");
            return;
        }

        if (isAsaasPix) {
            setLoading(true);
            try {
                const response = await PaymentService.createCreditRecharge({
                    studentLogin: client.login,
                    parentLogin: payerLogin,
                    clientCode: client.client_code || client.clientCode,
                    amount: sanitizedCredit,
                    method: "PIX",
                });

                if (response?.qrCodeEncodedImage) {
                    setPixQr({
                        qrCodeEncodedImage: response.qrCodeEncodedImage,
                        qrCodePayload: response.qrCodePayload,
                    });
                } else {
                    toast.error("Erro ao gerar QR PIX.");
                }
            } catch (error) {
                console.error(error);
                toast.error("Erro ao gerar QR PIX.");
            } finally {
                setLoading(false);
            }
            return;
        }

        // Non-Asaas methods require an open caixa
        if (!isOpened) {
            alert("Abra o caixa para usar este método de pagamento.");
            return;
        }

        setLoading(true);

        const updatedCreditCharge = {
            paymentMethod: selectedPayment,
            value: sanitizedCredit,
            received_value: parseFloat(receivedValue || sanitizedCredit),
            credit_afterwards: newValue,
            client_code: client.client_code ? client.client_code : client.clientCode,
            customer_id: client.login,
            transactionDate: getCurrentTimestamp(),
            transaction_type: "Credit Recharge Admin",
            transactionSource: "MANUAL"
        };

        try {
            if (client.role === 'STUDENT') {
                await CustomerService.updateStudentCredit(
                    client.login,
                    {
                        login: client.login,
                        credit: newValue.toFixed(2),
                        client_code: client.client_code
                    },
                    updatedCreditCharge
                );
            } else if (client.role === 'EMPLOYEE') {
                await CustomerService.updateEmployeeCredit(
                    client.login,
                    {
                        login: client.login,
                        credit: newValue.toFixed(2),
                        clientCode: client.client_code ? client.client_code : client.clientCode
                    },
                    updatedCreditCharge
                );
            }
            openReceipt(updatedCreditCharge);
            toast.success("Recarga realizada com sucesso!");
            onClose();
        } catch (error) {
            console.error(error);
            toast.error("Erro ao realizar recarga.");
        } finally {
            setLoading(false);
        }
    };

    const copyPixPayload = () => {
        if (!pixQr?.qrCodePayload) return;
        navigator.clipboard.writeText(pixQr.qrCodePayload);
        toast.success("Código PIX copiado!");
    };

    const showCaixaFechado = !initLoading && !asaasEnabled && !isOpened;

    return (
        <div className="fixed inset-0 z-60 bg-black/50 flex items-center justify-center animate-in fade-in duration-200">
            <div className="bg-white rounded-lg w-full max-w-xl p-8 shadow-lg transform transition-all">
                {initLoading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : showCaixaFechado ? (
                    <div className="h-full flex flex-col items-center justify-center text-center py-8">
                        <p className="text-4xl font-bold mb-4 text-gray-400">Caixa Fechado</p>
                        <p className="mb-6 text-gray-500">Para realizar recargas, abra o caixa no menu financeiro.</p>
                        <Link to="/financeiro" onClick={onClose}>
                            <CustomButton buttonType="confirm" text="Ir para o Caixa" />
                        </Link>
                        <button onClick={onClose} className="mt-4 text-sm text-gray-400 hover:text-gray-600 underline">Fechar</button>
                    </div>
                ) : pixQr ? (
                    <div className="flex flex-col items-center gap-4">
                        <h2 className="text-xl font-semibold text-center">Escaneie o QR Code PIX</h2>
                        <p className="text-sm text-gray-500 text-center">
                            Peça ao responsável para escanear o código. O crédito será adicionado automaticamente após o pagamento.
                        </p>

                        {pixQr.qrCodeEncodedImage && (
                            <img
                                src={`data:image/png;base64,${pixQr.qrCodeEncodedImage}`}
                                alt="QR Code PIX"
                                className="w-52 h-52 border border-gray-200 rounded"
                            />
                        )}

                        {pixQr.qrCodePayload && (
                            <div className="flex items-center gap-2 w-full border border-gray-200 rounded p-2 bg-gray-50">
                                <span className="flex-1 text-xs text-gray-700 truncate">{pixQr.qrCodePayload}</span>
                                <button
                                    onClick={copyPixPayload}
                                    className="text-xs text-blue-600 hover:underline shrink-0"
                                >
                                    Copiar
                                </button>
                            </div>
                        )}

                        <CustomButton
                            buttonType="cancel"
                            text="Fechar"
                            onClick={onClose}
                            fullWidth
                        />

                        {asaasEnabled && <AsaasSeal size="sm" />}
                    </div>
                ) : (
                    <>
                        <h2 className="text-2xl font-semibold text-center mb-6">Recarga de Crédito</h2>

                        <div className="space-y-4">
                            <p className="text-lg">
                                Valor atual: <span className={initialCredit >= 0 ? "text-green-600 font-bold" : "text-red-500 font-bold"}>{formatCurrency(initialCredit)}</span>
                            </p>

                            <div className="flex justify-between items-center gap-4">
                                <span>Valor a ser carregado</span>
                                <input
                                    type="number"
                                    min="0.01"
                                    step="0.05"
                                    className="w-1/3 p-2 border-b border-gray-300 focus:border-blue-500 outline-none transition-colors"
                                    onChange={(e) => setEditedCredit(e.target.value)}
                                    placeholder="R$ 0,00"
                                />
                            </div>

                            {!isAsaasPix && (
                                <div className="flex justify-between items-center gap-4">
                                    <span>Valor recebido</span>
                                    <input
                                        type="number"
                                        min="0.01"
                                        step="0.05"
                                        className="w-1/3 p-2 border-b border-gray-300 focus:border-blue-500 outline-none transition-colors"
                                        onChange={(e) => setReceivedValue(e.target.value)}
                                        placeholder="R$ 0,00"
                                    />
                                </div>
                            )}

                            <div className="flex items-center justify-between gap-4 mt-4">
                                <span>Forma de pagamento</span>
                                <div className="w-1/3">
                                    <PaymentSelect
                                        enabledValues={enabledPayments}
                                        value={selectedPayment}
                                        onChange={setSelectedPayment}
                                    />
                                </div>
                            </div>

                            {isAsaasPix && parents.length > 1 && (
                                <div className="flex items-center justify-between gap-4">
                                    <span>Responsável</span>
                                    <select
                                        className="w-1/2 p-2 border-b border-gray-300 focus:border-blue-500 outline-none transition-colors text-sm"
                                        value={selectedParentLogin}
                                        onChange={(e) => setSelectedParentLogin(e.target.value)}
                                    >
                                        <option value="" disabled>Selecione...</option>
                                        {parents.map(p => (
                                            <option key={p.login} value={p.login}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {isAsaasPix && (
                                <p className="text-xs text-blue-600">
                                    O crédito será adicionado automaticamente após confirmação do pagamento.
                                </p>
                            )}
                        </div>

                        <div className="flex justify-end gap-4 mt-8">
                            <CustomButton
                                buttonType="save"
                                text={loading ? "Processando..." : isAsaasPix ? "Gerar QR PIX" : "Finalizar"}
                                onClick={handleSaveClick}
                                disabled={loading}
                                fullWidth
                            />
                            <CustomButton
                                buttonType="cancel"
                                text="Cancelar"
                                onClick={onClose}
                                disabled={loading}
                                fullWidth
                            />
                        </div>

                        {isAsaasPix && (
                            <div className="flex justify-center mt-4">
                                <AsaasSeal size="sm" />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default CreditRechargeModal;
